import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { api } from '@/api/client';
import type { Page } from '@/api/types';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Empty } from '@/components/States';
import { AppliedChips, FilterButton, ListBody, ListScaffold, Pager, SearchBox, Segments, ViewChips } from './kit/List';
import { FilterSheet } from './kit/Sheet';
import { Thumb, productCover } from './kit/Thumb';
import { useInfinite, usePagedList, useResource, useUrlState } from './kit/hooks';
import { useCatalogSegments } from './nav';
import { downloadGalleryImages, loadGalleryImages, printGallery, shareGalleryImages } from './galleryShare';
import type { GalleryItem, GalleryStats } from './types';
import type { ProductCategory } from './types';
import s from './GalleryPage.module.css';

/**
 * 视图 = 老接口 image 参数（src/app/api/products/gallery/route.ts）。
 * 画册是给客户看的，默认只看有图的（高保真 R4：缺图不进画册）；「全部」与老版默认一致。
 */
const VIEWS: { key: string; label: string; count: (st: GalleryStats) => number }[] = [
  { key: 'withImage', label: '有图片', count: (st) => st.withImage },
  { key: 'multipleImages', label: '多图产品', count: (st) => st.multipleImages },
  { key: 'withoutImage', label: '缺图片', count: (st) => st.withoutImage },
  { key: 'all', label: '全部', count: (st) => st.all },
];

const UNCATEGORIZED = '__uncategorized__';

export function GalleryPage() {
  const can = useSession((st) => st.can);
  if (!can('gallery.read')) return <Empty icon="lock" title="没有查看产品画册的权限">需要「产品画册 · 查看」权限，请联系管理员。</Empty>;
  return <Gallery />;
}

function Gallery() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const segments = useCatalogSegments();
  const [sp, set] = useUrlState();
  const view = VIEWS.find((v) => v.key === sp.get('view')) || VIEWS[0];
  const cat = sp.get('cat') || undefined;
  const q = sp.get('q') || '';
  const [panel, setPanel] = useState(false);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Map<string, GalleryItem>>(new Map());
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [prepared, setPrepared] = useState<File[] | null>(null);
  const [display, setDisplay] = useState<'grid' | 'list'>('grid');
  const [imageCategoryCounts, setImageCategoryCounts] = useState<Record<string, number>>({});
  const { data: categoryData } = useResource<Page<ProductCategory>>('/api/product-categories?pageSize=500');
  const categories = categoryData?.items || [];

  const desk = ff === 'desktop';
  const tablet = ff === 'tablet';
  const query = useMemo(() => ({ image: view.key === 'all' ? undefined : view.key, category: cat, keyword: q || undefined }), [view, cat, q]);
  const list = usePagedList<GalleryItem, GalleryStats>('/api/products/gallery', query, desk ? 30 : 24, !desk);
  useInfinite(sentinel, !desk, list.loadMore, list.items.length);
  const stats = list.stats;
  const categoryKey = categories.map((c) => `${c.id}:${c.name}`).join('|');
  useEffect(() => {
    if (!categories.length) { setImageCategoryCounts({}); return; }
    const controller = new AbortController();
    void Promise.all(categories.map(async (category) => {
      const result = await api.get<Page<GalleryItem>>('/api/products/gallery', {
        image: 'withImage', category: category.name, keyword: q || undefined, page: 1, pageSize: 1,
      }, controller.signal);
      return [category.name, result.pagination.total] as const;
    })).then((entries) => setImageCategoryCounts(Object.fromEntries(entries)))
      .catch((error) => { if ((error as Error).name !== 'AbortError') setImageCategoryCounts({}); });
    return () => controller.abort();
  }, [categoryKey, q]);
  const canOpen = can('products.read');
  const picked = [...selected.values()];
  const allLoadedSelected = list.items.length > 0 && list.items.every((item) => selected.has(item.id));
  const toggle = (item: GalleryItem) => setSelected((old) => { const next = new Map(old); if (next.has(item.id)) next.delete(item.id); else next.set(item.id, item); return next; });
  const selectLoaded = () => setSelected((old) => { const next = new Map(old); list.items.forEach((item) => { if (allLoadedSelected) next.delete(item.id); else next.set(item.id, item); }); return next; });
  const stopSelecting = () => { setSelecting(false); setSelected(new Map()); setShareOpen(false); setShareError(''); };
  const openShare = async () => {
    if (!picked.length) return;
    setShareOpen(true); setShareError(''); setPrepared(null); setShareBusy(true);
    try { setPrepared(await loadGalleryImages(picked)); }
    catch (error) { setShareError(error instanceof Error ? error.message : '图片准备失败'); }
    finally { setShareBusy(false); }
  };
  const runShare = async (action: 'images' | 'download' | 'print') => {
    if (!picked.length || shareBusy) return;
    setShareError('');
    if (action === 'print') { if (!printGallery(picked)) setShareError('浏览器阻止了打印预览窗口，请允许弹出窗口后重试。'); else setShareOpen(false); return; }
    if (!prepared) { setShareError('图片尚未准备完成，请稍后重试。'); return; }
    setShareBusy(true);
    try { if (action === 'images') await shareGalleryImages(prepared, picked); else await downloadGalleryImages(prepared, picked); setShareOpen(false); }
    catch (error) { if ((error as Error).name !== 'AbortError') setShareError(error instanceof Error ? error.message : '分享失败'); }
    finally { setShareBusy(false); }
  };
  const printCurrent = () => {
    const items = picked.length ? picked : list.items;
    if (items.length) printGallery(items);
  };
  const catLabel = (v: string) => stats?.categories.find((c) => c.value === v)?.label || (v === UNCATEGORIZED ? '未分类' : v);

  const desktopSidebar = desk ? (
    <aside className={s.categorySidebar} aria-label="画册分类">
      <h2>分类</h2>
      <button type="button" className={`${s.categoryItem} ${!cat && view.key === 'withImage' ? s.categoryOn : ''}`} aria-pressed={!cat && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: undefined })}>
        <span><Icon name="book" />全部</span><b className="tnum">{stats?.withImage ?? 0}</b>
      </button>
      {(stats?.categories || []).map((category) => (
        <button key={category.value} type="button" className={`${s.categoryItem} ${cat === category.value && view.key === 'withImage' ? s.categoryOn : ''}`} aria-pressed={cat === category.value && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: category.value })}>
          <span><Icon name="box" />{category.label}</span><b className="tnum">{imageCategoryCounts[category.value] ?? 0}</b>
        </button>
      ))}
      <div className={s.categoryRule} />
      <button type="button" className={`${s.categoryItem} ${view.key === 'withoutImage' ? s.categoryOn : ''}`} aria-pressed={view.key === 'withoutImage'} onClick={() => set({ view: 'withoutImage', cat: undefined })}>
        <span><Icon name="alert" className={s.missingIcon} />缺图片</span><b className="tnum">{stats?.withoutImage ?? 0}</b>
      </button>
      <button type="button" className={`${s.categoryItem} ${view.key === 'multipleImages' ? s.categoryOn : ''}`} aria-pressed={view.key === 'multipleImages'} onClick={() => set({ view: 'multipleImages', cat: undefined })}>
        <span><Icon name="img" />多图产品</span><b className="tnum">{stats?.multipleImages ?? 0}</b>
      </button>
    </aside>
  ) : undefined;

  const filtered = !!(q || cat);
  const empty = filtered ? (
    <Empty icon="img" title="没有符合条件的产品">换个关键词，或者清空筛选再看看。</Empty>
  ) : view.key === 'withImage' ? (
    <Empty icon="img" title="还没有带图片的产品" action={<Button onClick={() => set({ view: 'all' })}>看全部产品</Button>}>
      图片在老版的产品编辑里上传；缺图的产品不进画册。
    </Empty>
  ) : (
    <Empty icon="img" title={`「${view.label}」里没有产品`}>换个视图看看。</Empty>
  );

  const displaySwitch = <div className={s.displayToggle}><button type="button" className={display === 'grid' ? s.on : ''} onClick={() => setDisplay('grid')}>大图</button><button type="button" className={display === 'list' ? s.on : ''} onClick={() => setDisplay('list')}>列表</button></div>;
  const gallerySearch = desk ? <div className={s.galleryToolbar}><SearchBox value={q} placeholder="产品名称 / 编号" onSubmit={(v) => set({ q: v })} />{displaySwitch}</div> : <SearchBox value={q} placeholder="产品名称 / 编号 / 分类" onSubmit={(v) => set({ q: v })} />;
  const tabletCategories = <div className={s.tabletCategoryRow} aria-label="画册分类">
    <div className={s.tabletCategoryChips}>
      <button type="button" className={!cat && view.key === 'withImage' ? s.tabletChipOn : ''} aria-pressed={!cat && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: undefined })}>全部 <b>{stats?.withImage ?? 0}</b></button>
      {categories.map((category) => <button type="button" key={category.id} className={cat === category.name && view.key === 'withImage' ? s.tabletChipOn : ''} aria-pressed={cat === category.name && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: category.name })}>{category.name} <b>{imageCategoryCounts[category.name] ?? 0}</b></button>)}
      <button type="button" className={view.key === 'withoutImage' ? s.tabletChipOn : ''} aria-pressed={view.key === 'withoutImage'} onClick={() => set({ view: 'withoutImage', cat: undefined })}>缺图片 <b>{stats?.withoutImage ?? 0}</b></button>
    </div>
    {displaySwitch}
  </div>;
  const tabletActions = selecting
    ? <div className={s.selectionActions}><button type="button" onClick={stopSelecting}>取消</button><button type="button" disabled={!selected.size} onClick={() => void openShare()}>分享 {selected.size ? selected.size : ''}</button></div>
    : <div className={`${s.selectionActions} ${s.printActions}`}><button type="button" onClick={() => setSelecting(true)}>选择</button><button type="button" disabled={!picked.length && !list.items.length} onClick={printCurrent}>打印</button><button type="button" disabled={!picked.length && !list.items.length} onClick={printCurrent}>导出 PDF</button></div>;

  const grid = (
    <ListBody state={{ loading: list.loading, error: list.error, count: list.items.length }} errorTitle="画册加载失败" detail="GET /api/products/gallery" onRetry={list.reload} empty={empty} skeleton={2}>
      {((!desk && !tablet) || selecting) && <div className={s.bar}>
        <span>{selecting ? `已选 ${selected.size} 个产品` : `${list.total} 个产品${q ? ` · 搜索「${q}」` : ''}`}</span>
        {stats && view.key === 'withImage' && stats.withoutImage > 0 && <span>{stats.withoutImage} 个缺图不进画册</span>}
        {desk && selecting && <><button type="button" onClick={stopSelecting}>取消</button><button type="button" disabled={!selected.size} onClick={() => void openShare()}>分享 {selected.size ? selected.size : ''}</button></>}
        {selecting && <button type="button" onClick={selectLoaded}>{allLoadedSelected ? '取消当前已加载' : '全选已加载'}</button>}
        {ff !== 'phone' && !desk && !tablet && displaySwitch}
      </div>}
      <div className={display === 'list' && ff !== 'phone' && !desk ? s.list : s.grid}>
        {list.items.map((p) => {
          const inner = (
            <>
          <Thumb src={productCover(p, true)} alt={p.name} className={s.galleryThumb} fallbackIcon="box" />
          <b>{p.name}</b>
          <small><span className="tnum">{p.code}</span><span>{p.category || '未分类'}</span></small>
          <div className={s.categoryTag}>{p.category || '未分类'}</div>
              {p.imageCount > 1 && <i className="tnum">{p.imageCount} 张</i>}
            </>
          );
          return selecting ? <button type="button" key={p.id} className={`${s.card} ${s.selectCard} ${selected.has(p.id) ? s.selected : ''}`} aria-pressed={selected.has(p.id)} onClick={() => toggle(p)}><span className={s.check}>{selected.has(p.id) ? '✓' : ''}</span>{inner}</button> : canOpen ? <Link key={p.id} to={`/products/${p.id}`} className={s.card}>{inner}</Link> : <div key={p.id} className={s.card}>{inner}</div>;
        })}
      </div>
      {!desk && <div ref={setSentinel} className={s.more}>{list.hasNext ? (list.loading ? '加载中…' : '上滑加载更多') : '没有更多了'}</div>}
    </ListBody>
  );

  return (
    <ListScaffold
      title={desk || tablet ? '产品画册' : '产品资料'}
      subtitle={desk || tablet ? <>给客户看的视图，只显示有图的产品 <span className="tnum">{stats?.withImage ?? 0} 个有图 · {stats?.withoutImage ?? 0} 个缺图</span></> : undefined}
      total={desk ? undefined : list.total}
      actions={desk ? <div className={`${s.selectionActions} ${s.printActions}`}><button type="button" disabled={!picked.length && !list.items.length} onClick={printCurrent}>导出 PDF</button><button type="button" disabled={!picked.length && !list.items.length} onClick={printCurrent}>打印</button></div> : tablet ? tabletActions : <div className={s.selectionActions}>{selecting ? <><button type="button" onClick={stopSelecting}>取消</button><button type="button" disabled={!selected.size} onClick={() => void openShare()}>分享 {selected.size ? selected.size : ''}</button></> : <button type="button" onClick={() => setSelecting(true)}>选择</button>}</div>}
      segments={ff === 'phone' ? <Segments items={segments} /> : undefined}
      chips={!desk ? tablet ? tabletCategories : ff === 'phone' ? <div className={s.phoneCategoryChips} aria-label="画册分类">
        <button type="button" className={!cat && view.key === 'withImage' ? s.phoneChipOn : ''} aria-pressed={!cat && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: undefined })}>全部 <b>{stats?.withImage ?? 0}</b></button>
        {categories.map((category) => <button type="button" key={category.id} className={cat === category.name && view.key === 'withImage' ? s.phoneChipOn : ''} aria-pressed={cat === category.name && view.key === 'withImage'} onClick={() => set({ view: undefined, cat: category.name })}>{category.name} <b>{imageCategoryCounts[category.name] ?? 0}</b></button>)}
        <button type="button" className={view.key === 'withoutImage' ? s.phoneChipOn : ''} aria-pressed={view.key === 'withoutImage'} onClick={() => set({ view: 'withoutImage', cat: undefined })}>缺图片 <b>{stats?.withoutImage ?? 0}</b></button>
      </div> : <ViewChips items={VIEWS.map((v) => ({ key: v.key, label: v.label, count: stats ? v.count(stats) : undefined }))} value={view.key} onChange={(k) => set({ view: k === 'withImage' ? undefined : k, cat: undefined })} /> : undefined}
      sidebar={desktopSidebar}
      searchAction={ff === 'phone'}
      filterBtn={ff === 'phone' ? undefined : <FilterButton count={cat ? 1 : 0} onClick={() => setPanel(true)} />}
      applied={!tablet ? <AppliedChips items={cat ? [{ key: 'cat', label: catLabel(cat) }] : []} onRemove={() => set({ cat: undefined })} onClear={() => set({ cat: undefined })} /> : undefined}
      search={gallerySearch}
      searching={!!q}
      onRefresh={!desk ? list.reload : undefined}
      list={grid}
      pager={desk && list.items.length > 0 ? <Pager page={list.page} size={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGo={list.goto} /> : undefined}
      overlay={<>{panel && (
        <FilterSheet
          value={{ cat }}
          groups={[{ key: 'cat', title: '分类', opts: categories.map((category) => ({ value: category.name, label: category.name, count: imageCategoryCounts[category.name] ?? 0 })) }]}
          hint="画册只带图片、名称、编号和分类，不显示库存和价格。"
          onClose={() => setPanel(false)}
          onApply={(v) => { setPanel(false); set({ cat: v.cat }); }}
        />
      )}{shareOpen && <div className={s.shareBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !shareBusy) setShareOpen(false); }}><section className={s.shareMenu} role="dialog" aria-modal="true" aria-label="分享产品"><header><h2>分享 {picked.length} 个产品</h2><button type="button" onClick={() => setShareOpen(false)} disabled={shareBusy}>×</button></header><p>只带图片、名称、编号和分类，不带库存或价格。{shareBusy && !prepared ? '图片准备中…' : '全选只包含已加载的产品。'}</p><button type="button" disabled={shareBusy || !prepared} onClick={() => void runShare('images')}>发送图片给客户 <small>系统分享</small></button><button type="button" disabled={shareBusy} onClick={() => void runShare('print')}>生成 PDF 画册 <small>打印对话框中选择“存储为 PDF”</small></button><button type="button" disabled={shareBusy || !prepared} onClick={() => void runShare('download')}>下载图片 <small>打包为 ZIP，可存到文件</small></button>{shareError && <p role="alert" className={s.shareError}>{shareError}</p>}</section></div>}{selecting && selected.size > 0 && ff === 'phone' && !shareOpen && <div className={s.mobileSelection}><span>已选 <b>{selected.size}</b> 个产品</span><button type="button" onClick={() => { if (!printGallery(picked)) { setShareOpen(true); setShareError('浏览器阻止了打印预览窗口。'); } }}>生成 PDF</button><button type="button" onClick={() => void openShare()}>分享</button></div>}</>}
    />
  );
}
